/**
 * Shared SEO utility for managing document title, meta tags, canonical URLs,
 * Open Graph tags, and JSON-LD structured data.
 *
 * All functions are designed to be used inside React useEffect hooks.
 * They clean up after themselves on unmount to prevent stale tags.
 */

import noindexConfig from './noindex.json';

const SITE_URL = 'https://usekora.app';
const SITE_NAME = 'Kora';

/**
 * True for pages that stay live but must not be indexed: the listed paths, the listed prefixes, and every
 * /compare/<slug> that is not in compareIndexed. Also read by scripts/generate-sitemap.mjs.
 */
export function isNoindexPath(rawPath: string): boolean {
  const path = rawPath.replace(/\/+$/, '') || '/';
  if (noindexConfig.paths.includes(path)) return true;
  if (noindexConfig.prefixes.some((p) => path === p || path.startsWith(`${p}/`))) return true;
  const m = path.match(/^\/compare\/([^/]+)$/);
  return !!m && !noindexConfig.compareIndexed.includes(m[1]);
}

/** Set the document title and meta description. */
export function setDocumentMeta(title: string, description: string) {
  document.title = title;

  upsertMeta('description', description);
  upsertMeta('robots', isNoindexPath(window.location.pathname) ? 'noindex, follow' : 'index, follow');
  upsertMeta('og:title', title, true);
  upsertMeta('og:description', description, true);
  upsertMeta('og:site_name', SITE_NAME, true);
  upsertMeta('og:type', 'website', true);
  // Social platforms ignore SVG images, so the default is a real 1200x630 PNG.
  upsertMeta('og:image', `${SITE_URL}/assets/og-default.png`, true);
  upsertMeta('og:image:width', '1200', true);
  upsertMeta('og:image:height', '630', true);
  upsertMeta('og:image:alt', 'Kora: the pay-per-ticket AI helpdesk. $0.10 per ticket, no seats, no monthly plans.', true);
  upsertMeta('twitter:card', 'summary_large_image');
  upsertMeta('twitter:title', title);
  upsertMeta('twitter:description', description);
  upsertMeta('twitter:image', `${SITE_URL}/assets/og-default.png`);
  upsertMeta('twitter:image:alt', 'Kora: the pay-per-ticket AI helpdesk.');
}

/** Set the canonical URL for the current page. */
export function setCanonical(path: string) {
  const url = path.startsWith('http') ? path : `${SITE_URL}${path}`;
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = url;

  // Also set og:url
  upsertMeta('og:url', url, true);
}

/** Set the OG image for the current page. */
export function setOgImage(imagePath: string) {
  const url = imagePath.startsWith('http') ? imagePath : `${SITE_URL}${imagePath}`;
  upsertMeta('og:image', url, true);
  upsertMeta('twitter:image', url);
}

/**
 * Inject JSON-LD structured data into the page.
 * Returns a cleanup function that removes the script tag.
 */
export function injectJsonLd(data: object | object[]): () => void {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  script.setAttribute('data-seo-jsonld', 'true');
  document.head.appendChild(script);

  return () => {
    script.remove();
  };
}

/**
 * Inject multiple JSON-LD blocks. Returns a single cleanup function.
 */
export function injectJsonLdList(datas: object[]): () => void {
  const cleanups = datas.map((data) => injectJsonLd(data));
  return () => cleanups.forEach((cleanup) => cleanup());
}

// ─── Schema builders ──────────────────────────────────────

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/assets/logo_square.svg`,
    description:
      'AI helpdesk and issue tracker priced per ticket: $0.10 per ticket, unlimited members, no monthly plans.',
    areaServed: 'Global',
    knowsLanguage: 'en',
  };
}

export function buildWebPageSchema(path: string, title: string, description: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: `${SITE_URL}${path}`,
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL,
    },
  };
}

export function buildSoftwareApplicationSchema(name: string, description: string, offers?: object[]) {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description,
    offers,
  };
  return schema;
}

export function buildOfferSchema(price: number, priceCurrency: string, description: string, url?: string) {
  return {
    '@type': 'Offer',
    price: price.toString(),
    priceCurrency,
    description,
    url: url || SITE_URL,
  };
}

export function buildFaqPageSchema(faqs: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };
}

export function buildItemListSchema(items: { name: string; url: string; description?: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: item.url,
      ...(item.description ? { description: item.description } : {}),
    })),
  };
}

export function buildBreadcrumbSchema(breadcrumbs: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
}

export function buildTechArticleSchema(headline: string, description: string, datePublished: string, dateModified?: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline,
    description,
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/assets/logo_square.svg` },
    },
    datePublished,
    ...(dateModified ? { dateModified } : {}),
  };
}

// ─── Internal helpers ──────────────────────────────────────

type MetaAttr = 'name' | 'property';

function upsertMeta(key: string, content: string, isProperty = false): void {
  const attr: MetaAttr = isProperty ? 'property' : 'name';
  let meta = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attr, key);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}
